'use client';

import { useState, useEffect, useCallback, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import {
  FileText,
  ChevronRight,
  ExternalLink,
  Home,
  RefreshCw,
} from 'lucide-react';
import { NavigationButtons } from '@/components/NavigationButtons';
import clsx from 'clsx';
import { ConfluenceRenderer } from '@/components/ConfluenceRenderer';
import ConfluenceTree from '@/components/ConfluenceTree';
import { AtlassianConnectionBanner } from '@/components/AtlassianConnectionBanner';
import type { ConfluencePage, ConfluenceBreadcrumb, ConfluenceViewMode } from '@/types/confluence';

function ConfluencePageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pageId = searchParams.get('pageId');

  const [viewMode, setViewMode] = useState<ConfluenceViewMode>('tree');
  const [activePage, setActivePage] = useState<ConfluencePage | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<ConfluenceBreadcrumb[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch page content
  const fetchPageContent = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/confluence/page/${id}`);
      if (res.ok) {
        const data = await res.json();
        setActivePage(data.page);
        setBreadcrumbs(data.breadcrumbs || []);
      }
    } catch (error) {
      console.error('Error fetching page:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle page selection
  const handleSelectPage = (id: string) => {
    router.push(`/confluence?pageId=${id}`);
  };

  // Handle view mode change
  const handleViewModeChange = (mode: ConfluenceViewMode) => {
    setViewMode(mode);
  };

  useEffect(() => {
    if (pageId) {
      fetchPageContent(pageId);
    } else {
      setActivePage(null);
      setBreadcrumbs([]);
    }
  }, [fetchPageContent, pageId]);

  return (
    <div className="flex h-screen bg-white font-sans overflow-hidden">
      {/* Sidebar with Tree */}
      <div className="w-80 border-r border-gray-200 flex flex-col bg-gray-50">
        <div className="p-4 border-b border-gray-200 flex items-center gap-3 bg-white">
          <NavigationButtons />
          <h1 className="font-bold text-gray-800">Confluence</h1>
        </div>

        <ConfluenceTree
          selectedPageId={pageId || undefined}
          onSelectPage={handleSelectPage}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <div className="border-b border-gray-200 bg-white p-4">
          <Suspense fallback={null}>
            <AtlassianConnectionBanner product="confluence" compact />
          </Suspense>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : activePage ? (
          <>
            {/* Header with Breadcrumbs */}
            <div className="border-b border-gray-200 bg-white">
              {/* Breadcrumbs */}
              {breadcrumbs.length > 0 && (
                <div className="px-6 pt-4 flex items-center gap-1 text-sm text-gray-500">
                  <button
                    onClick={() => handleSelectPage(breadcrumbs[0]?.id)}
                    className="hover:text-blue-600 flex items-center gap-1"
                  >
                    <Home className="w-3.5 h-3.5" />
                  </button>
                  {breadcrumbs.map((crumb, idx) => (
                    <span key={crumb.id} className="flex items-center gap-1">
                      <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                      <button
                        onClick={() => handleSelectPage(crumb.id)}
                        className={clsx(
                          'hover:text-blue-600 truncate max-w-[150px]',
                          idx === breadcrumbs.length - 1 && 'text-gray-700 font-medium'
                        )}
                      >
                        {crumb.title}
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Title */}
              <div className="p-6 pt-3 flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{activePage.title}</h2>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    {activePage.labels && activePage.labels.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        {activePage.labels.slice(0, 5).map(label => (
                          <span
                            key={label}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                          >
                            {label}
                          </span>
                        ))}
                        {activePage.labels.length > 5 && (
                          <span className="text-xs text-gray-400">
                            +{activePage.labels.length - 5}
                          </span>
                        )}
                      </div>
                    )}
                    {activePage.is_orphan && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs flex items-center gap-1">
                        고아 페이지
                      </span>
                    )}
                  </div>
                </div>
                <a
                  href={activePage.web_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  Confluence에서 열기 <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 bg-white">
              <ConfluenceRenderer content={activePage.body_storage} pageId={activePage.id} />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>사이드바에서 페이지를 선택하세요</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Loading fallback for Suspense
function LoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );
}

// Main export with Suspense boundary
export default function ConfluencePageView() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <ConfluencePageContent />
    </Suspense>
  );
}
